(() => {
  const dialogId = 'SfVideoLightbox';
  const progressThresholds = [25, 50, 75, 100];

  let initialTrigger = null;
  let activeCard = null;
  let activeCards = [];
  let activeIndex = -1;
  let previewStates = new Map();
  let progressByCard = new Map();
  let restoreFocusAfterClose = true;

  const getDialog = () => document.getElementById(dialogId);

  const getVisiblePreview = (card) => {
    const videos = Array.from(card.querySelectorAll('.section-multitile__media video'));

    return videos.find((video) => {
      const media = video.closest('.section-multitile__media');
      return media && window.getComputedStyle(media).display !== 'none';
    }) || videos[0];
  };

  const getVideoSource = (video) => {
    return video.currentSrc || video.getAttribute('src') || video.querySelector('source')?.getAttribute('src');
  };

  const getConfiguredMedia = (card, preview) => {
    const useMobile = window.matchMedia('(max-width: 749px)').matches;
    const source =
      (useMobile && card.dataset.sfVideoSrcMobile) ||
      card.dataset.sfVideoSrc ||
      (preview ? getVideoSource(preview) : '');
    const poster =
      (useMobile && card.dataset.sfVideoPosterMobile) ||
      card.dataset.sfVideoPoster ||
      preview?.poster ||
      '';

    return { source, poster };
  };

  const isVisibleCard = (card) => {
    return card.getClientRects().length > 0 && window.getComputedStyle(card).display !== 'none';
  };

  const getSectionCards = (trigger) => {
    const section = trigger.closest('.section-multitile--video-lightbox');
    if (!section) return [];

    return Array.from(section.querySelectorAll('.sf-video-card'))
      .filter((card) => isVisibleCard(card) && getConfiguredMedia(card, getVisiblePreview(card)).source);
  };

  const pausePreview = (preview) => {
    if (!preview || previewStates.has(preview)) return;

    previewStates.set(preview, !preview.paused);
    preview.pause();
  };

  const restorePreviews = () => {
    previewStates.forEach((wasPlaying, preview) => {
      if (wasPlaying && preview.isConnected) {
        preview.play().catch(() => {});
      }
    });
    previewStates = new Map();
  };

  const analyticsPayload = (action, card, extra = {}) => ({
    event: `sf_shoppable_video_${action}`,
    action,
    section_id: card?.dataset.sfVideoSectionId || '',
    block_id: card?.dataset.sfVideoBlockId || '',
    video_title: card?.dataset.sfVideoTitle || '',
    video_position: activeIndex + 1,
    video_count: activeCards.length,
    product_id: card?.dataset.sfProductId || '',
    product_handle: card?.dataset.sfProductHandle || '',
    ...extra,
  });

  const track = (action, card = activeCard, extra = {}) => {
    if (!card) return;

    const payload = analyticsPayload(action, card, extra);
    window.dispatchEvent(new CustomEvent('sf:shoppable-video', { detail: payload }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      const publishResult = window.Shopify?.analytics?.publish?.(payload.event, payload);
      publishResult?.catch?.(() => {});
    } catch (_error) {
      // Analytics must never interrupt video playback or navigation.
    }
  };

  const renderProduct = (card) => {
    const dialog = getDialog();
    const frame = dialog?.querySelector('[data-sf-video-frame]');
    const productRegion = dialog?.querySelector('[data-sf-video-product]');
    const productTemplate = card.querySelector('[data-sf-video-product-template]');

    if (!frame || !productRegion) return;

    productRegion.replaceChildren();

    if (!productTemplate) {
      productRegion.hidden = true;
      frame.classList.remove('sf-video-lightbox__frame--shoppable');
      return;
    }

    productRegion.appendChild(productTemplate.content.cloneNode(true));
    productRegion.hidden = false;
    frame.classList.add('sf-video-lightbox__frame--shoppable');
  };

  const updateNavigation = () => {
    const dialog = getDialog();
    const previousButton = dialog?.querySelector('[data-sf-video-previous]');
    const nextButton = dialog?.querySelector('[data-sf-video-next]');
    const counter = dialog?.querySelector('[data-sf-video-counter]');
    const hasMultipleVideos = activeCards.length > 1;

    if (previousButton) {
      previousButton.hidden = !hasMultipleVideos;
      previousButton.disabled = activeIndex <= 0;
    }

    if (nextButton) {
      nextButton.hidden = !hasMultipleVideos;
      nextButton.disabled = activeIndex >= activeCards.length - 1;
    }

    if (counter) {
      counter.hidden = !hasMultipleVideos;
      counter.textContent = hasMultipleVideos ? `Video ${activeIndex + 1} of ${activeCards.length}` : '';
    }
  };

  const loadCard = (card, navigationDirection = '') => {
    const dialog = getDialog();
    const player = dialog?.querySelector('.sf-video-lightbox__video');
    const title = dialog?.querySelector('[data-sf-video-title]');
    const preview = getVisiblePreview(card);
    const { source, poster } = getConfiguredMedia(card, preview);

    if (!dialog || !player || !source) return false;

    const previousCard = activeCard;
    activeCard = card;
    activeIndex = Math.max(0, activeCards.indexOf(card));
    pausePreview(preview);

    player.pause();
    player.removeAttribute('src');
    player.removeAttribute('poster');
    player.load();
    player.src = source;
    if (poster) player.poster = poster;
    player.muted = false;
    player.defaultMuted = false;
    player.volume = 1;

    if (preview && Number.isFinite(preview.currentTime)) {
      player.currentTime = preview.currentTime;
    }

    const videoTitle = card.dataset.sfVideoTitle || 'Customer video';
    player.setAttribute('aria-label', videoTitle);
    if (title) title.textContent = videoTitle;

    renderProduct(card);
    updateNavigation();

    if (navigationDirection && previousCard && previousCard !== card) {
      track('navigation', card, {
        direction: navigationDirection,
        previous_block_id: previousCard.dataset.sfVideoBlockId || '',
      });
    }

    track('view', card);
    player.play().catch(() => {});
    return true;
  };

  const cleanUpPlayer = () => {
    const dialog = getDialog();
    const player = dialog?.querySelector('.sf-video-lightbox__video');
    const productRegion = dialog?.querySelector('[data-sf-video-product]');
    const frame = dialog?.querySelector('[data-sf-video-frame]');
    const triggerToRestore = initialTrigger;

    if (activeCard && player) {
      track('close', activeCard, {
        video_seconds: Number.isFinite(player.currentTime) ? Math.round(player.currentTime) : 0,
      });
    }

    if (player) {
      player.pause();
      player.removeAttribute('src');
      player.removeAttribute('poster');
      player.load();
    }

    productRegion?.replaceChildren();
    if (productRegion) productRegion.hidden = true;
    frame?.classList.remove('sf-video-lightbox__frame--shoppable');

    document.documentElement.classList.remove('sf-video-lightbox-open');
    restorePreviews();

    initialTrigger = null;
    activeCard = null;
    activeCards = [];
    activeIndex = -1;
    progressByCard = new Map();

    if (restoreFocusAfterClose && triggerToRestore?.isConnected) {
      window.requestAnimationFrame(() => triggerToRestore.focus({ preventScroll: true }));
    }

    restoreFocusAfterClose = true;
  };

  const closePlayer = ({ restoreFocus = true } = {}) => {
    const dialog = getDialog();
    if (!dialog) return;

    restoreFocusAfterClose = restoreFocus;

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      cleanUpPlayer();
    }
  };

  const openPlayer = (trigger) => {
    const dialog = getDialog();
    const card = trigger.closest('.sf-video-card');
    if (!dialog || !card) return;

    activeCards = getSectionCards(trigger);
    if (!activeCards.length) activeCards = [card];
    initialTrigger = trigger;
    progressByCard = new Map();
    restoreFocusAfterClose = true;

    document.documentElement.classList.add('sf-video-lightbox-open');

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    if (!loadCard(card)) closePlayer();
  };

  const navigate = (offset) => {
    const nextIndex = activeIndex + offset;
    if (nextIndex < 0 || nextIndex >= activeCards.length) return;

    loadCard(activeCards[nextIndex], offset > 0 ? 'next' : 'previous');
  };

  const reportProgress = () => {
    const dialog = getDialog();
    const player = dialog?.querySelector('.sf-video-lightbox__video');
    if (!player || !activeCard || !Number.isFinite(player.duration) || player.duration <= 0) return;

    const percentage = Math.min(100, Math.floor((player.currentTime / player.duration) * 100));
    const cardKey = activeCard.dataset.sfVideoBlockId || activeCard.dataset.sfVideoSrc;
    const reported = progressByCard.get(cardKey) || new Set();

    progressThresholds.forEach((threshold) => {
      if (percentage >= threshold && !reported.has(threshold)) {
        reported.add(threshold);
        track('progress', activeCard, {
          video_percent: threshold,
          video_seconds: Math.round(player.currentTime),
        });
      }
    });

    progressByCard.set(cardKey, reported);
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const openTrigger = event.target.closest('[data-sf-video-open]');
    if (openTrigger) {
      event.preventDefault();
      openPlayer(openTrigger);
      return;
    }

    if (event.target.closest('[data-sf-video-close]')) {
      closePlayer();
      return;
    }

    if (event.target.closest('[data-sf-video-previous]')) {
      navigate(-1);
      return;
    }

    if (event.target.closest('[data-sf-video-next]')) {
      navigate(1);
      return;
    }

    const productLink = event.target.closest('[data-sf-video-product-link]');
    if (productLink) {
      track('product_click', activeCard);

      const destination = new URL(productLink.href, window.location.href);
      const isSameDocument =
        destination.origin === window.location.origin
        && destination.pathname === window.location.pathname
        && destination.search === window.location.search;
      const anchorId = isSameDocument && destination.hash
        ? decodeURIComponent(destination.hash.slice(1))
        : '';
      const anchorTarget = anchorId ? document.getElementById(anchorId) : null;

      closePlayer({ restoreFocus: false });

      if (anchorTarget) {
        event.preventDefault();
        if (window.location.hash !== destination.hash) {
          window.history.pushState(null, '', destination.hash);
        }

        window.requestAnimationFrame(() => {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          anchorTarget.scrollIntoView({
            behavior: reducedMotion ? 'auto' : 'smooth',
            block: 'start',
          });

          const bundleControl = anchorTarget.querySelector(
            '[data-sf-bundle-choice]:checked, [data-sf-bundle-choice], button:not([disabled]), a[href]'
          );
          bundleControl?.focus({ preventScroll: true });
        });
      }

      return;
    }

    const dialog = getDialog();
    if (dialog && event.target === dialog) {
      closePlayer();
    }
  });

  document.addEventListener('keydown', (event) => {
    const dialog = getDialog();
    if (!dialog?.hasAttribute('open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closePlayer();
      return;
    }

    if (event.target instanceof HTMLVideoElement) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      navigate(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      navigate(1);
    }
  }, true);

  const initialize = () => {
    const dialog = getDialog();
    const player = dialog?.querySelector('.sf-video-lightbox__video');

    dialog?.addEventListener('close', cleanUpPlayer);
    player?.addEventListener('timeupdate', reportProgress);
    player?.addEventListener('ended', reportProgress);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
